import {Box, TypographyOwnProps} from '@mui/material';
import {t} from 'i18next';
import React, {useEffect, useRef, useState} from 'react';
import {ReactComponent as ChevLeft} from '../../../../src/assests/svg/icon_chevleft.svg';
import {ReactComponent as ChevRight} from '../../../../src/assests/svg/icon_chevright.svg';
import {
  ALERT_CARD_WIDTH,
  ALERT_CAROUSEL_CARD_TRANSLATEBY_SIZE,
} from '../../../constants/constants';
import AurButton from '../aur-button/AurButton';
import AurAlert, {AurAlertDataType} from './AurAlert';

interface AurAlertCaroselProps {
  alertsData: AurAlertDataType[];
  titleAlignment?: TypographyOwnProps['align'];
}

const CARD_WIDTH = ALERT_CARD_WIDTH; // Fixed card width in pixels

const AurAlertCarousel: React.FC<AurAlertCaroselProps> = (
  props: AurAlertCaroselProps
) => {
  const {alertsData, titleAlignment} = props;
  const [currentPage, setCurrentPage] = useState(0);
  const [alertContainerWidth, setAlertContainerWidth] = useState<number>(0);

  useEffect(() => {
    const element = alertCarouselRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      setAlertContainerWidth(element.offsetWidth);
    });

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // Handler to go to the previous page
  const handlePrev = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  // Handler to go to the next page
  const handleNext = () => {
    if (currentPage < alertsData.length) setCurrentPage(currentPage + 1);
  };

  const translateX = currentPage * ALERT_CAROUSEL_CARD_TRANSLATEBY_SIZE;
  const alertCarouselRef = useRef<HTMLDivElement>(null);

  const numberOfAdjustableAlerts = Math.floor(
    alertContainerWidth / (CARD_WIDTH + 24)
  );

  const showControls = alertsData.length > numberOfAdjustableAlerts;
  const numberOfAlertPages = alertsData.length - numberOfAdjustableAlerts + 1;

  return (
    <div
      className="aur-alert-carousel-main-container"
      role="region"
      aria-roledescription="carousel"
      ref={alertCarouselRef}
    >
      <div
        className="aur-alert-carousel-container"
        data-testid="aur-alert-carousel-container"
        style={{
          transform: `translateX(-${translateX}px)`,
          transition: 'transform 0.5s ease-in-out',
          width: `60vw`,
        }}
      >
        {alertsData.map((alert: AurAlertDataType) => (
          <AurAlert
            titleAlignment={titleAlignment}
            key={alert.userAlertId}
            alertTitle={alert.alertTitle}
            triggeredOn={alert?.triggeredOn as string}
            alertDescription={alert?.alertDescription as string}
            showDatasets={alert.showDatasets}
            showArrowCTA={alert.showArrowCTA}
            projectName={alert.projectName}
            workspaceName={alert.workspaceName}
            alertType={alert.alertType}
            budgetDataLength={alert.budgetDataLength}
            datasets={alert.datasets}
            deleteButtonHandler={alert?.deleteButtonHandler}
            prevButtonHandler={alert?.prevButtonHandler}
            onPressNavigationHandler={alert?.onPressNavigationHandler}
            onPressAlertHandler={alert?.onPressAlertHandler}
          />
        ))}
      </div>
      {showControls && (
        <div className="aur-alert-carousel-bottom-container">
          <div className="aur-alert-carousel-pagination-dots-container">
            {Array.from({length: numberOfAlertPages}).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor:
                    currentPage === index ? '#FFFFFF' : '#4C6181',
                  transition: 'background-color 0.3s ease',
                }}
              />
            ))}
          </div>
          <div className="aur-alert-carousel-button-container">
            <AurButton
              type="continue"
              size="Small"
              classNames="aur-alert-carousel-button-icon"
              children={
                <ChevLeft
                  aria-label={t('reports.hostingUserSummary.chevronLeftHints')}
                  aria-hidden="true"
                />
              }
              onClick={handlePrev}
              ariaLabel={t('common.alert.clickToSeePreviousSlide')}
              disabled={currentPage === 0}
              dataTestId="aur-alert-carousel-prev-button"
            />
            <AurButton
              type="continue"
              size="Small"
              classNames="aur-alert-carousel-button-icon"
              children={
                <ChevRight
                  aria-label={t('reports.hostingUserSummary.chevronRightHints')}
                  aria-hidden="true"
                />
              }
              onClick={handleNext}
              ariaLabel={t('common.alert.clickToSeeNextSlide')}
              disabled={currentPage === numberOfAlertPages - 1}
              dataTestId="aur-alert-carousel-next-button"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AurAlertCarousel;
